import { supabase } from "../supabaseClient";
import { logger } from "../utils/logger";

const PAGE_SIZE = 50;
// How far back the Component dropdown looks for the values it offers. The list has
// to come from a query of its own: deriving it from the filtered rows would mean
// that picking component X leaves X as the only option you could then pick.
const COMPONENT_SAMPLE_SIZE = 1000;

// PostgREST parses commas and parentheses as filter syntax, so a search term goes
// inside double quotes with any quote or backslash escaped. % and _ stay live as
// ilike wildcards, which only ever widens a match.
const quoteForFilter = (term) => `"%${term.replace(/["\\]/g, "\\$&")}%"`;

export async function fetchLogs({ severity, component, dateFrom, dateTo, search, offset }) {
  let query = supabase.from("app_logs").select("*").order("created_at", { ascending: false });

  if (severity && severity !== "All") query = query.eq("severity", severity.toLowerCase());
  if (component && component !== "All") query = query.eq("component", component);
  // Date inputs are plain YYYY-MM-DD and created_at is a timestamptz; bounding the
  // whole UTC day keeps the same day-granularity the old string compare had.
  if (dateFrom) query = query.gte("created_at", `${dateFrom}T00:00:00Z`);
  if (dateTo) query = query.lte("created_at", `${dateTo}T23:59:59.999Z`);
  if (search) {
    // details_text is a generated column (details::text) with a trigram index,
    // since PostgREST can't filter inside jsonb directly.
    const quoted = quoteForFilter(search);
    query = query.or(`action.ilike.${quoted},component.ilike.${quoted},details_text.ilike.${quoted}`);
  }

  const { data, error } = await query.range(offset, offset + PAGE_SIZE - 1);

  if (error) {
    logger.error('logsService', 'fetch_logs_failed', { severity, component, dateFrom, dateTo, search, offset, error: error.message });
    throw error;
  }

  const rows = data || [];
  return { rows, hasMore: rows.length === PAGE_SIZE };
}

export async function fetchComponentOptions() {
  const { data, error } = await supabase
    .from("app_logs")
    .select("component")
    .order("created_at", { ascending: false })
    .limit(COMPONENT_SAMPLE_SIZE);

  if (error) {
    logger.error('logsService', 'fetch_component_options_failed', { error: error.message });
    throw error;
  }

  return Array.from(new Set((data || []).map((r) => r.component).filter(Boolean))).sort();
}

export async function purgeLogs() {
  const cutoff = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
  const { error } = await supabase
    .from("app_logs")
    .delete()
    .lt("created_at", cutoff);

  if (error) {
    logger.error('logsService', 'logs_purge_failed', { cutoff, error: error.message });
    throw error;
  }
  logger.info('logsService', 'logs_purged', { cutoff });
}
